import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Container,
	Paper,
	Typography,
	Box,
	Alert,
	CircularProgress,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PaymentForm from "../../components/PaymentForm";
import FirstTimeUserForm from "../../components/FirstTimeUserForm";
import ProgressStepper from "../../components/ProgressStepper";
import { locations } from "../../utils/locaitons.util";
import { updateUserInfo } from "../../services/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function PaymentCapture() {
	const location = useLocation();
	const navigate = useNavigate();
	const [error, setError] = useState(null);
	const [isStripeLoading, setIsStripeLoading] = useState(true);

	// Get SetupIntent, userId, locationId, lockId, and booth data from navigation state
	const setupIntent = location.state?.setupIntent;
	const userId = location.state?.userId;
	const locationId = location.state?.locationId;
	const lockId = location.state?.lockId;
	const boothId = location.state?.boothId;
	const boothData = location.state?.boothData;
	const isFirstTimeUser = location.state?.isFirstTimeUser;
	const userData = location.state?.userData || {};

	useEffect(() => {
		// For booth flow, we need boothData, for legacy flow we need valid location/lock
		const isValidLegacyFlow =
			locationId &&
			lockId &&
			locations.isValidLocationAndLock(locationId, lockId);
		const isValidBoothFlow = boothData && locationId && lockId;

		if (!setupIntent || !userId || (!isValidLegacyFlow && !isValidBoothFlow)) {
			navigate("/register");
		}
	}, [setupIntent, userId, navigate, locationId, lockId, boothData]);

	if (!setupIntent || !userId) return null;

	const options = {
		clientSecret: setupIntent,
		appearance: { theme: "stripe" },
		loader: "auto",
	};

	const handleFirstTimeUserSuccess = async (result) => {
		try {
			// Update user information in the database
			await updateUserInfo({
				userId,
				firstName: result.userData.firstName,
				lastName: result.userData.lastName,
				email: result.userData.email,
			});

			// Navigate to session setup
			navigate("/session/new", {
				state: {
					userId,
					locationId,
					lockId,
					paymentMethodId: result.paymentMethodId,
					...(boothId && { boothId }),
					...(boothData && { boothData }),
				},
			});
		} catch (error) {
			setError("Failed to update user information. Please try again.");
		}
	};

	const handleExistingUserSuccess = (result) => {
		navigate("/session/new", {
			state: {
				userId,
				locationId,
				lockId,
				paymentMethodId: result.paymentMethodId,
				...(boothId && { boothId }),
				...(boothData && { boothData }),
			},
		});
	};

	// Show first-time user form for new users
	if (isFirstTimeUser) {
		return (
			<FirstTimeUserForm
				setupIntent={setupIntent}
				userData={userData}
				onSuccess={handleFirstTimeUserSuccess}
				onError={setError}
			/>
		);
	}

	// Show regular payment form for existing users
	return (
		<Container maxWidth="sm">
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Paper elevation={3} sx={{ p: 4, width: "100%" }}>
					<ProgressStepper currentStep={2} />
					<Typography variant="h4" component="h1" gutterBottom align="center">
						Add Payment Method
					</Typography>
					<Typography
						variant="body1"
						color="text.secondary"
						align="center"
						gutterBottom
					>
						Your card will not be charged yet
					</Typography>
					<Typography
						variant="body2"
						color="text.secondary"
						align="center"
						gutterBottom
						sx={{ mb: 3 }}
					>
						All payments are securely processed by Stripe
					</Typography>

					{error && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{error}
						</Alert>
					)}

					<Elements stripe={stripePromise} options={options}>
						<PaymentForm
							onError={setError}
							onSuccess={handleExistingUserSuccess}
							clientSecret={setupIntent}
							onReady={() => setIsStripeLoading(false)}
						/>
					</Elements>

					{isStripeLoading && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								p: 2,
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
							}}
						>
							<CircularProgress />
						</Box>
					)}
				</Paper>
			</Box>
		</Container>
	);
}
